/**
 * The Rules pane: what is law, what was, and how to find any of it.
 *
 * FROM `design_handoff_rules_ia`, 26 August. Two changes to how the rulebook is browsed, and the
 * second is the one that matters:
 *
 *   `By year` STOPS BEING A THIRD TAB. It was never a different corpus — it was the same 132
 *   rules in a different order — so a tab for it made the page ask "which list?" when the only
 *   real question is "which rule?". It becomes an ORDERING, and the rail swaps from topics to
 *   years underneath it.
 *
 *   STATUS BECOMES A FILTER, which is what makes 41 superseded and 7 rescinded rules reachable
 *   from the rulebook at all. Until now the pane showed the 84 in force and nothing else, and the
 *   other 48 were reachable only by knowing an id and typing it into the URL.
 *
 * The nine `failed-proposal` records stay out of every list at every setting, search included.
 * `rulebook.json`'s own note says so: they are kept to record what the league considered and
 * rejected, and they are not rules.
 */

import { RULES, type RulebookEntry } from './rulebook.js';
import { RULE_GROUPS, inGroup } from './ruleGroups.js';

/** What a rule's `status` means to a reader, which is not quite what the data calls it. */
export type StatusFilter = 'active' | 'superseded' | 'rescinded' | 'all';

export const STATUS_FILTERS: ReadonlyArray<{ id: StatusFilter; label: string }> = [
  { id: 'active', label: 'In force' },
  { id: 'superseded', label: 'Replaced' },
  { id: 'rescinded', label: 'Rescinded' },
  { id: 'all', label: 'All' },
];

export type Ordering = 'topic' | 'year';

/** A rule the league passed. The nine it rejected are not rules and never appear. */
export const isLaw = (r: RulebookEntry): boolean => r.status !== 'failed-proposal';

/** Every rule the league has ever passed, law or not, newest first. */
/**
 * The 132 the league has ever passed — from the effective corpus when one is supplied.
 *
 * The parameter is what makes committing the record visible here. `RULES` is bundled data and
 * cannot know what the committee did last night; `effectiveRules` folds the committed outcomes
 * over it. Defaulting to the bundle keeps every existing caller honest and unchanged.
 */
export const passedRules = (rules: readonly RulebookEntry[] = RULES): RulebookEntry[] =>
  rules.filter(isLaw);

export function matchesStatus(r: RulebookEntry, status: StatusFilter): boolean {
  return status === 'all' ? true : r.status === status;
}

/*
 * THE CORPUS IS A PARAMETER FROM HERE DOWN, defaulting to the bundle.
 *
 * Everything on this pane counts, filters and rails off the same list, so a pane that shows what
 * the committee did last night has to be given that list once rather than have each function
 * reach for `RULES` on its own. The default keeps every existing caller — and every test — reading
 * the bundled corpus unchanged.
 */
export const statusCounts = (
  rules: readonly RulebookEntry[] = RULES,
): Record<StatusFilter, number> => {
  const law = passedRules(rules);
  return {
    active: law.filter((r) => r.status === 'active').length,
    superseded: law.filter((r) => r.status === 'superseded').length,
    rescinded: law.filter((r) => r.status === 'rescinded').length,
    all: law.length,
  };
};

/**
 * SEARCH IGNORES THE STATUS FILTER, and this is the one rule in this file worth arguing for.
 *
 * `route.ts` records that a bare rule id routes to this page and that twenty `§` citations across
 * the app depend on landing somebody on the rule they name. Three of those ids point at rules that
 * are no longer in force — 2020-I-5 among them, which still carries the operative text for the
 * rename-refusal fine because the amendment that superseded it only moved a blowout threshold.
 *
 * A citation that answers "not found" because of a chip the reader did not notice they had set is
 * the exact failure the citations were built to avoid. So the query runs across all 132, and an
 * out-of-scope hit is returned with its status for the row to mark.
 */
export const SEARCH_FROM = 2;

export function matchesSearch(r: RulebookEntry, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (needle.length < SEARCH_FROM) return false;
  return `${r.id} ${r.title} ${r.summary} ${r.text}`.toLowerCase().includes(needle);
}

export const searching = (q: string): boolean => q.trim().length >= SEARCH_FROM;

export interface PaneQuery {
  status: StatusFilter;
  order: Ordering;
  /** The selected rail row: a group id under `topic`, a year under `year`. */
  topic?: string;
  year?: number;
  q?: string;
  /** The effective corpus. Defaults to the bundled rulebook where a caller has none. */
  rules?: readonly RulebookEntry[];
}

/**
 * The rules to draw, in the order to draw them.
 *
 * Newest first within a group, which is the order the rulebook itself is kept in and the order
 * somebody scanning for "what changed recently" expects.
 */
export function rulesForPane(query: PaneQuery): RulebookEntry[] {
  const q = query.q ?? '';
  const byRecency = (a: RulebookEntry, b: RulebookEntry) =>
    (b.year - a.year) || a.id.localeCompare(b.id);

  if (searching(q)) {
    return passedRules(query.rules).filter((r) => matchesSearch(r, q)).sort(byRecency);
  }

  return passedRules(query.rules)
    .filter((r) => matchesStatus(r, query.status))
    .filter((r) => {
      if (query.order === 'year') return query.year === undefined || r.year === query.year;
      const group = RULE_GROUPS.find((g) => g.id === query.topic);
      return !group || group.topics.length === 0 ? true : inGroup(r, group);
    })
    .sort(byRecency);
}

export interface RailRow {
  id: string;
  label: string;
  /** Under the ACTIVE status filter. Zero is information, not a reason to hide the row. */
  count: number;
}

/**
 * The rail, whichever thing it is listing.
 *
 * Counts are computed under the live status filter rather than against the whole corpus, so
 * "Trades 19" becomes "Trades 0" when the reader switches to Rescinded — which is the answer to
 * "has a trades rule ever been rescinded", given without them having to click to find out. The
 * row stays tappable; the pane it opens says so in words.
 */
export function railFor(
  order: Ordering, status: StatusFilter, rules: readonly RulebookEntry[] = RULES,
): RailRow[] {
  const law = passedRules(rules).filter((r) => matchesStatus(r, status));
  if (order === 'year') {
    const years = [...new Set(passedRules(rules).map((r) => r.year))].sort((a, b) => b - a);
    return years.map((y) => ({
      id: String(y),
      label: String(y),
      count: law.filter((r) => r.year === y).length,
    }));
  }
  return RULE_GROUPS
    .filter((g) => g.topics.length > 0)
    .map((g) => ({ id: g.id, label: g.label, count: law.filter((r) => inGroup(r, g)).length }));
}

/** The line at the right of the filter row, which says what the reader is looking at. */
export function scopeNote(status: StatusFilter, q = ''): string {
  if (searching(q)) return 'Searching everything';
  switch (status) {
    case 'active': return 'Showing only what is law today';
    case 'superseded':
    case 'rescinded': return 'These are not in force';
    default: return 'Everything the league has ever passed, law or not';
  }
}

export interface EmptyState { title: string; body: string }

/**
 * WHAT AN EMPTY PANE SAYS, and it is never nothing.
 *
 * A filter and a topic that meet on zero rules is a real answer to a real question — no trades
 * rule has ever been rescinded — and falling through to a bare "End of topic" tail turns that
 * answer into a screen that looks broken.
 */
export function emptyStateFor(query: PaneQuery): EmptyState | null {
  const q = query.q ?? '';
  if (rulesForPane(query).length > 0) return null;

  if (searching(q)) {
    return {
      title: `Nothing matches “${q.trim()}”`,
      body: `Covers every one of the ${statusCounts().all} rules the league has passed — `
        + 'ids, titles and full text.',
    };
  }

  const label = query.order === 'year'
    ? String(query.year ?? '')
    : (RULE_GROUPS.find((g) => g.id === query.topic)?.label ?? '').toLowerCase();
  const written = rulesForPane({ ...query, status: 'all' }).length;
  const inForce = rulesForPane({ ...query, status: 'active' }).length;
  const word = STATUS_FILTERS.find((s) => s.id === query.status)?.label.toLowerCase() ?? '';

  return {
    title: query.order === 'year'
      ? `Nothing from ${label} is ${word}`
      : `No ${label} rule is ${word}`,
    body: written === 0
      ? 'Nothing has been written here at all.'
      : `Of the ${written} written ${query.order === 'year' ? 'that year' : 'on this topic'}, `
        + `${inForce} ${inForce === 1 ? 'is' : 'are'} in force. `
        + 'Switch the filter above to read them.',
  };
}
